import type { ExecutedAction } from "@/types_api/sofia/product/ExecutedAction"

import type { SofiaActionKind } from "../model/constants"
import type { SofiaProposedAction } from "../model/types"

export function isPythonMetricAction(kind: SofiaActionKind): boolean {
  return (
    kind === "create_python_metric_draft" ||
    kind === "preview_python_metric" ||
    kind === "cancel_python_metric_preview" ||
    kind === "publish_python_metric" ||
    kind === "save_python_analysis_metric"
  )
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function identifier(value: unknown): string | null {
  return typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 128 &&
    !/[^A-Za-z0-9_-]/.test(value)
    ? value
    : null
}

function previewStatus(value: unknown): string {
  const status = typeof value === "string" ? value.toUpperCase() : ""
  switch (status) {
    case "QUEUED":
    case "IN QUEUE":
    case "PENDING":
    case "RUNNING":
    case "IN PROGRESS":
      return "result pending"
    case "COMPLETED":
    case "FAILED":
    case "CANCELLED":
    case "SKIPPED":
    case "DELETED":
      return `preview ${status.toLowerCase()}`
    default:
      return "result status unavailable"
  }
}

/** Preserve scoped-read identities without echoing source or free-form diagnostics. */
export function buildPythonMetricReceipt(
  proposal: SofiaProposedAction,
  executed: ExecutedAction,
  orgPath: (path: string) => string | undefined,
): { ok: true; detail: string; href?: string } {
  const result = record(executed.result_preview)
  if (proposal.kind === "create_python_metric_draft") {
    const draft = record(result.draft)
    return {
      ok: true,
      detail: `Python draft created; not yet tested or published. draft_id=${identifier(draft.draft_id) ?? "unavailable"}; version_id=${identifier(draft.version_id) ?? "unavailable"}`,
    }
  }
  if (
    proposal.kind === "publish_python_metric" ||
    proposal.kind === "save_python_analysis_metric"
  ) {
    const metricId = identifier(result.metric_id)
    return {
      ok: true,
      detail: `${proposal.kind === "save_python_analysis_metric" ? "Metric created from the tested audio analysis" : "Tested Python metric published"}. metric_id=${metricId ?? "unavailable"}; version_id=${identifier(result.version_id) ?? "unavailable"}`,
      href: orgPath(
        metricId ? `/metrics/${encodeURIComponent(metricId)}` : "/metrics",
      ),
    }
  }
  // The public preview endpoint accepts exactly one conversation per request.
  const previews = result.previews
  const preview =
    Array.isArray(previews) && previews.length === 1 ? record(previews[0]) : {}
  const outcome =
    proposal.kind === "cancel_python_metric_preview"
      ? "Preview cancellation processed"
      : "Python preview submitted"
  return {
    ok: true,
    detail: `${outcome}; ${previewStatus(preview.status)}. draft_id=${identifier(proposal.args.draft_id) ?? "unavailable"}; preview_id=${identifier(preview.preview_id) ?? "unavailable"}`,
  }
}
